import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { AiSettingsView, ProposalView } from '../types'

const BASE = '/v1/ai'

export function useAiSettings(enabled = true) {
  return useQuery({
    queryKey: ['ai', 'settings'],
    enabled,
    queryFn: async () => (await apiClient.get<AiSettingsView>(`${BASE}/settings`)).data,
  })
}

export interface SaveAiSettings {
  enabled: boolean
  provider: string
  baseUrl: string | null
  /** Omit the key entirely to leave the stored one alone; send "" to clear it. */
  apiKey?: string
  model: string | null
  allowedDocumentTypes: string[]
  redactTextBeforeCall: boolean
  maxFileBytes: number
  minConfidence: number

  assistantEnabled: boolean
  assistantProvider: string
  assistantModel: string | null
  assistantToolGroups: string[]
  assistantMaxOutputTokens: number
  assistantDataResidencyNote: string | null
  assistantTurnsPerHour: number
}

export function useAiSettingsMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: SaveAiSettings) =>
      (await apiClient.put<AiSettingsView>(`${BASE}/settings`, body)).data,
    onSuccess: (saved) => queryClient.setQueryData(['ai', 'settings'], saved),
  })
}

export function useAiProposals(enabled = true) {
  return useQuery({
    queryKey: ['ai', 'proposals'],
    enabled,
    queryFn: async () => (await apiClient.get<ProposalView[]>(`${BASE}/proposals`)).data,
  })
}

export interface ConfirmExtraction {
  proposalId: string
  employeeId: string
  documentTypeId: string
  documentNumber: string | null
  issuingCountry: string | null
  issuingAuthority: string | null
  issueDate: string | null
  expiryDate: string | null
  storedFileId: string | null
  notes: string | null
}

export function useExtractionMutations(employeeId?: string) {
  const queryClient = useQueryClient()
  // A confirmed proposal writes an employee_document, so the documents list is stale too —
  // and it is the list the person is looking at when they press Confirm.
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['ai'] })
    void queryClient.invalidateQueries({ queryKey: ['documents'] })
    if (employeeId) {
      void queryClient.invalidateQueries({ queryKey: ['documents', 'employee', employeeId] })
    }
  }

  return {
    extract: useMutation({
      mutationFn: async (body: {
        storedFileId: string
        documentTypeId: string
        employeeId?: string
      }) => (await apiClient.post<ProposalView>(`${BASE}/extract`, body)).data,
    }),
    confirm: useMutation({
      mutationFn: async (body: ConfirmExtraction) =>
        (await apiClient.post<ProposalView>(`${BASE}/confirm`, body)).data,
      onSuccess: invalidate,
    }),
    reject: useMutation({
      mutationFn: async (proposalId: string) =>
        (await apiClient.post<ProposalView>(`${BASE}/reject`, { proposalId })).data,
      onSuccess: invalidate,
    }),
  }
}
